import { z } from 'zod';

export const PLAN_IDS = ['starter', 'growth', 'business', 'enterprise'] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const PLAN_LABEL: Record<PlanId, string> = {
  starter: 'Starter',
  growth: 'Growth',
  business: 'Business',
  enterprise: 'Enterprise',
};

export const KNOWLEDGE_SOURCE_TYPES = ['text', 'file', 'url', 'api', 'mcp'] as const;
export type KnowledgeSourceType = (typeof KNOWLEDGE_SOURCE_TYPES)[number];

export const FILE_TYPES = ['pdf', 'docx', 'txt', 'md', 'xlsx', 'csv'] as const;
export type FileType = (typeof FILE_TYPES)[number];

const count = z.number().int().min(0).max(1_000_000);

/**
 * Every entitlement is a dotted key with a validated value. Plans and per-tenant overrides store
 * rows of (key, value); unknown keys are rejected and missing keys fall back to closed defaults.
 */
export const ENTITLEMENT_SCHEMAS = {
  'knowledge.sources.text': z.boolean(),
  'knowledge.sources.file': z.boolean(),
  'knowledge.sources.url': z.boolean(),
  'knowledge.sources.api': z.boolean(),
  'knowledge.sources.mcp': z.boolean(),
  'knowledge.fileTypes': z.array(z.enum(FILE_TYPES)),
  'knowledge.maxFileMb': count,
  'knowledge.maxDocuments': count,
  'knowledge.storageMb': count,
  'knowledge.processedMbPerMonth': count,
  'knowledge.url.maxSources': count,
  'knowledge.url.maxPagesPerSource': count,
  'knowledge.url.refresh': z.enum(['none', 'weekly', 'daily']),
  'knowledge.api.maxConnectors': count,
  'knowledge.api.minRefreshHours': count,
  'knowledge.mcp.maxServers': count,
  'knowledge.shared': z.boolean(),
} as const;

export type EntitlementKey = keyof typeof ENTITLEMENT_SCHEMAS;
export type EntitlementValues = { [K in EntitlementKey]: z.infer<(typeof ENTITLEMENT_SCHEMAS)[K]> };
export const ENTITLEMENT_KEYS = Object.keys(ENTITLEMENT_SCHEMAS) as EntitlementKey[];

/** Fail closed: anything not granted by a plan or override is off / zero. */
export const CLOSED_ENTITLEMENTS: EntitlementValues = {
  'knowledge.sources.text': false,
  'knowledge.sources.file': false,
  'knowledge.sources.url': false,
  'knowledge.sources.api': false,
  'knowledge.sources.mcp': false,
  'knowledge.fileTypes': [],
  'knowledge.maxFileMb': 0,
  'knowledge.maxDocuments': 0,
  'knowledge.storageMb': 0,
  'knowledge.processedMbPerMonth': 0,
  'knowledge.url.maxSources': 0,
  'knowledge.url.maxPagesPerSource': 0,
  'knowledge.url.refresh': 'none',
  'knowledge.api.maxConnectors': 0,
  'knowledge.api.minRefreshHours': 0,
  'knowledge.mcp.maxServers': 0,
  'knowledge.shared': false,
};

export interface EffectiveEntitlements {
  plan: PlanId;
  values: EntitlementValues;
  /** Keys whose value comes from a per-tenant override. */
  overridden: EntitlementKey[];
}

export function parseEntitlementValue<K extends EntitlementKey>(
  key: K,
  value: unknown,
): EntitlementValues[K] {
  return ENTITLEMENT_SCHEMAS[key].parse(value) as EntitlementValues[K];
}

export function isEntitlementKey(key: string): key is EntitlementKey {
  return key in ENTITLEMENT_SCHEMAS;
}

/** defaults <- plan rows <- tenant overrides. Invalid stored values are ignored (stay closed). */
export function resolveEntitlements(
  plan: PlanId,
  planRows: { key: string; value: unknown }[],
  overrideRows: { key: string; value: unknown }[],
): EffectiveEntitlements {
  const values: Record<string, unknown> = { ...CLOSED_ENTITLEMENTS };
  const overridden: EntitlementKey[] = [];
  const apply = (rows: { key: string; value: unknown }[], isOverride: boolean) => {
    for (const { key, value } of rows) {
      if (!isEntitlementKey(key)) continue;
      const parsed = ENTITLEMENT_SCHEMAS[key].safeParse(value);
      if (!parsed.success) continue;
      values[key] = parsed.data;
      if (isOverride) overridden.push(key);
    }
  };
  apply(planRows, false);
  apply(overrideRows, true);
  return { plan, values: values as EntitlementValues, overridden };
}

/** Is the feature enabled (boolean) or non-zero (count)? Used to find the minimum plan for upsell copy. */
export function grants(values: EntitlementValues, key: EntitlementKey): boolean {
  const v = values[key];
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v > 0;
  if (Array.isArray(v)) return v.length > 0;
  return v !== 'none';
}

export function minimumPlanFor(
  matrix: Record<PlanId, EntitlementValues>,
  check: (values: EntitlementValues) => boolean,
): PlanId | null {
  return PLAN_IDS.find((p) => check(matrix[p])) ?? null;
}

export const setPlanSchema = z.object({ plan: z.enum(PLAN_IDS) });

export const setOverridesSchema = z.object({
  reason: z.string().trim().min(3, 'Explica el motivo del ajuste.').max(300),
  /** null removes the override (falls back to the plan value). */
  overrides: z.record(z.string(), z.unknown()),
});
